import { useEffect, useState } from 'react';
import {
  AVATAR_POSE_EVENT,
  getAvatarPose,
  projectHeadToScreen,
  STATIC_FALLBACK_HEAD_FRACTION,
} from '../../../services/avatarLayout';

const CANVAS_SELECTOR = '#canvas';
const STATIC_FALLBACK_SELECTOR = '.static-amira-fallback';
// A momentarily-unmeasurable rect (mid-layout) shouldn't kill the whole show cycle, so
// retry across a few animation frames before giving up.
const MAX_MEASURE_RETRIES = 5;

/**
 * Measure where Amira's head currently sits on screen and return viewport pixel
 * coordinates to anchor the thinking bubble to (TT-1166).
 *
 * Anchoring = the pose Coach PUBLISHED (getAvatarPose, i.e. exactly what Coach drew)
 * projected through the measured avatar-element rect. Coach freezes its world pose at
 * render/OSK-toggle time (no resize listener), so reading the published pose — rather than
 * re-deriving it from the current viewport — keeps the bubble aligned even across a
 * mid-session resize/rotation. The measured rect handles the viewport-scaling part.
 *
 * Re-measures on resize / orientationchange, when Coach applies a new pose
 * (AVATAR_POSE_EVENT), and when the static-fallback image finishes decoding; also retries
 * across a few frames if the rect isn't ready the instant the bubble first wants to show.
 *
 * @param {Object} opts
 * @param {boolean} opts.active - Only measure while the bubble is showing.
 * @param {boolean} opts.webglError - Anchor to the static PNG fallback instead of the canvas.
 * @param {string} opts.rig - Current tutor rig ('Amira' | 'Amira2'); selects the head offset.
 * @returns {{ left: number, top: number } | null} Anchor point, or null if not measurable.
 */
export function useAvatarAnchor({ active, webglError, rig }) {
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!active || typeof document === 'undefined') {
      setAnchor(null);
      return undefined;
    }

    let rafId = null;
    let cancelled = false;
    let fallbackImg = null;

    const computePoint = () => {
      const selector = webglError ? STATIC_FALLBACK_SELECTOR : CANVAS_SELECTOR;
      const el = document.querySelector(selector);
      if (!el) {
        return null;
      }
      const rect = el.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) {
        return null;
      }
      if (webglError) {
        return {
          left: rect.left + STATIC_FALLBACK_HEAD_FRACTION.x * rect.width,
          top: rect.top + STATIC_FALLBACK_HEAD_FRACTION.y * rect.height,
        };
      }
      const pose = getAvatarPose();
      if (!pose) {
        return null; // Coach hasn't applied a pose yet
      }
      return projectHeadToScreen(rect, rig, pose);
    };

    const cancelRaf = () => {
      if (rafId != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(rafId);
      }
      rafId = null;
    };

    const attempt = (retriesLeft) => {
      const point = computePoint();
      if (point) {
        setAnchor(point);
        return;
      }
      if (retriesLeft > 0 && typeof requestAnimationFrame === 'function') {
        rafId = requestAnimationFrame(() => {
          if (!cancelled) {
            attempt(retriesLeft - 1);
          }
        });
        return;
      }
      // Only clear on the FINAL give-up — a transient unmeasurable rect (mid-resize) must
      // not flicker an already-visible bubble off.
      setAnchor(null);
    };

    // Cancel any in-flight retry chain before starting a new measure so a stale retry
    // can't later clear an anchor a newer measure just set.
    const measure = () => {
      cancelRaf();
      attempt(MAX_MEASURE_RETRIES);
    };

    // The static fallback's div sizes from an auto-width PNG, so its rect is 0 until the
    // image decodes; re-measure on its load so the bubble isn't lost for that cycle.
    if (webglError) {
      const el = document.querySelector(STATIC_FALLBACK_SELECTOR);
      fallbackImg = el ? el.querySelector('img') : null;
      if (fallbackImg && !fallbackImg.complete) {
        fallbackImg.addEventListener('load', measure);
      }
    }

    // No scroll listener: the reader/tutor views the bubble appears in do not scroll, so
    // the fixed-position anchor (from getBoundingClientRect viewport coords) stays valid.
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.addEventListener(AVATAR_POSE_EVENT, measure);

    return () => {
      cancelled = true;
      cancelRaf();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.removeEventListener(AVATAR_POSE_EVENT, measure);
      if (fallbackImg) {
        fallbackImg.removeEventListener('load', measure);
      }
    };
  }, [active, webglError, rig]);

  return anchor;
}
